import { Warning } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { Formik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';
import { useCompanyController } from 'api/controllers/CompanyController';
import { useCounterPartyController } from 'api/controllers/CounterPartyController';
import { useUserController } from 'api/controllers/UserController';

import {
  ApprovalRule,
  ApprovalRuleCreate,
  ApprovalCondition,
  ApprovalConditionCreatePatch,
  ApprovalFlowCreatePatch,
  AmountConditionCreatePatch,
  AmountOperator,
  ApprovalMode,
  ConditionType,
  CounterpartyQueryFilters,
  CounterpartyType,
  CurrencyDropDown,
  InvoiceExpenseType,
  User,
  ExpenseOperator,
  CounterpartyConditionCreatePatch,
  CounterpartyOperator,
  Company
} from 'openapi';

import { UnsavedChangesSync } from 'components/shared/UnsavedChangesSync/UnsavedChangesSync';

import { useTranslations } from 'context/TranslationContext';
import { useUnsavedChanges } from 'context/UnsavedChangesContext';

import { useModal } from 'hooks/useModal';

import {
  formWrapperStyle,
  scrollableFormContainerStyle,
  stickyFormHeaderWrapperStyle,
  stickyLivePreviewWrapperStyle,
  stickyWrapperStyle,
  fallbackWarningContentStyle,
  fallbackWarningIconStyle
} from 'styles/pages/ApprovalRuleFormStyle';

import { ApprovalModeSection } from './components/ApprovalModeSection';
import { ApprovalRuleFormHeader } from './components/ApprovalRuleFormHeader';
import {
  ConditionsSection,
  type Condition
} from './components/ConditionsSection';
import { LivePreviewSection } from './components/LivePreviewSection';
import { RuleBasicInfoSection } from './components/RuleBasicInfoSection';
import { RuleOptionsSection } from './components/RuleOptionsSection';
import { ApprovalRuleSimulatorModal } from './simulator/ApprovalRuleSimulatorModal';

interface ApprovalRuleFormProps {
  initialValues?: ApprovalRule;
  selectedMode?: ApprovalMode;
  selectedApprovers?: User[];
  requiredApprovals?: number;
  exclusive?: boolean;
  active?: boolean;
  onSave: (values: ApprovalRuleCreate) => void;
  onCancel: () => void;
  onTestRules?: () => void;
  companyId: number;
  existingRules?: ApprovalRule[];
  isFallback?: boolean;
}

type ConditionError = {
  values?: string;
  amountFrom?: string;
  amountTo?: string;
};

type FormErrors = {
  name?: string;
  approvers?: string;
  conditions?: ConditionError[];
  conditionsDuplicate?: string;
};

export const ApprovalRuleForm = ({
  initialValues,
  selectedMode: initialSelectedMode,
  selectedApprovers: initialSelectedApprovers,
  requiredApprovals: initialRequiredApprovals,
  exclusive: initialExclusive,
  active: initialActive,
  onSave,
  onCancel,
  companyId,
  existingRules = [],
  isFallback = false
}: ApprovalRuleFormProps) => {
  const { translate } = useTranslations();
  const { requestNavigation } = useUnsavedChanges();

  const { getApprovers } = useUserController();
  const { getExpenseTypes, getCurrencies } = useAdditionalFieldsController();
  const { getFilteredCounterpartiesByCompanyId } = useCounterPartyController();
  const { getCompanyDetailsById } = useCompanyController();

  const isEdit = !!initialValues;
  const isFallbackRule = isFallback;
  const submitFormRef = useRef<(() => Promise<void>) | null>(null);

  const defaultMode = isFallbackRule
    ? ApprovalMode.SEQUENTIAL
    : ApprovalMode.AUTO_APPROVE;

  const [selectedMode, setSelectedMode] = useState<ApprovalMode>(
    initialSelectedMode || defaultMode
  );

  const conditionsStepNumber = isFallbackRule ? 1 : 2;
  const approvalModeStepNumber = isFallbackRule ? 2 : 3;
  const [approvers, setApprovers] = useState<User[]>();
  const [expenseTypes, setExpenseTypes] = useState<InvoiceExpenseType[]>();
  const [counterparties, setCounterparties] = useState<any[]>();
  const [currencies, setCurrencies] = useState<CurrencyDropDown[]>();
  const [company, setCompany] = useState<Company>();
  const [selectedApprovers, setSelectedApprovers] = useState<any[]>([]);
  const [requiredApprovals, setRequiredApprovals] = useState<number>(1);
  const [approversError, setApproversError] = useState<string>('');

  const {
    isOpen: isSimulatorOpen,
    openModal: openSimulator,
    closeModal: closeSimulator
  } = useModal();

  const findCurrencyByIsoCode = useCallback(
    (isoCode: string): CurrencyDropDown | undefined => {
      return currencies?.find((c) => c.isoCode === isoCode);
    },
    [currencies]
  );

  const fetchApprovers = useCallback(async () => {
    const data = await getApprovers(Number(companyId));
    setApprovers(data);
  }, [getApprovers, companyId]);

  const fetchExpenseTypes = useCallback(async () => {
    const data = await getExpenseTypes(Number(companyId));
    setExpenseTypes(data);
  }, [getExpenseTypes, companyId]);

  const fetchCounterparties = useCallback(async () => {
    const filters: CounterpartyQueryFilters = {
      types: [CounterpartyType.SUPPLIER]
    };
    const data = await getFilteredCounterpartiesByCompanyId(
      Number(companyId),
      filters
    );
    setCounterparties(data.counterparties);
  }, [getFilteredCounterpartiesByCompanyId, companyId]);

  const fetchCurrencies = useCallback(async () => {
    const data = await getCurrencies();
    setCurrencies(data);
  }, [getCurrencies]);

  const fetchCompany = useCallback(async () => {
    const data = await getCompanyDetailsById(Number(companyId));
    setCompany(data);
  }, [getCompanyDetailsById, companyId]);

  useEffect(() => {
    fetchApprovers();
    fetchExpenseTypes();
    fetchCounterparties();
    fetchCurrencies();
    fetchCompany();
  }, [
    fetchApprovers,
    fetchExpenseTypes,
    fetchCounterparties,
    fetchCurrencies,
    fetchCompany
  ]);

  useEffect(() => {
    if (initialSelectedMode) {
      setSelectedMode(initialSelectedMode);
    }
    if (initialSelectedApprovers) {
      setSelectedApprovers(initialSelectedApprovers);
    }
    if (initialRequiredApprovals !== undefined) {
      setRequiredApprovals(initialRequiredApprovals);
    }
  }, [initialSelectedMode, initialSelectedApprovers, initialRequiredApprovals]);

  useEffect(() => {
    if (
      (selectedMode === ApprovalMode.SEQUENTIAL ||
        selectedMode === ApprovalMode.PARALLEL) &&
      selectedApprovers.length === 0
    ) {
      const errorMessage =
        selectedMode === ApprovalMode.SEQUENTIAL
          ? translate('errors.sequentialModeRequiresApprover')
          : translate('errors.parallelModeRequiresApprover');
      setApproversError(errorMessage);
    } else {
      setApproversError('');
    }
  }, [selectedMode, selectedApprovers.length, approversError, translate]);

  const formikSetFieldValueRef = useRef<
    ((field: string, value: any) => void) | null
  >(null);

  useEffect(() => {
    if (selectedMode === ApprovalMode.AUTO_APPROVE) {
      formikSetFieldValueRef.current?.('exclusive', false);
    }
  }, [selectedMode]);

  const getApproversError = useCallback(
    (mode: ApprovalMode, approversCount: number): string => {
      if (
        (mode === ApprovalMode.SEQUENTIAL || mode === ApprovalMode.PARALLEL) &&
        approversCount === 0
      ) {
        return mode === ApprovalMode.SEQUENTIAL
          ? translate('errors.sequentialModeRequiresApprover')
          : translate('errors.parallelModeRequiresApprover');
      }
      return '';
    },
    [translate]
  );

  useEffect(() => {
    setApproversError(
      getApproversError(selectedMode, selectedApprovers.length)
    );
  }, [selectedMode, selectedApprovers.length, getApproversError]);

  const normalizeConditionKey = useCallback((condition: Condition): string => {
    if (condition.conditionType === ConditionType.AMOUNT) {
      const amountFrom = condition.values[0] || '0';
      const isBetween = condition.conditionOperator === AmountOperator.BETWEEN;
      const amountTo = isBetween ? condition.values[1] || '0' : '';
      const currency = isBetween
        ? condition.values[2] || 'EUR'
        : condition.values[1] || 'EUR';
      return `AMOUNT|${condition.conditionOperator}|${amountFrom}|${amountTo}|${currency}`;
    }

    if (condition.conditionType === ConditionType.EXPENSE) {
      const sortedIds = [...condition.values]
        .map((v) => v.id)
        .sort((a, b) => a - b);
      return `EXPENSE|${condition.conditionOperator}|${sortedIds}`;
    }

    if (condition.conditionType === ConditionType.COUNTERPARTY) {
      const ids = condition.values.map((v) => v.id);
      const sortedIds = [...ids].sort((a, b) => a - b);
      return `COUNTERPARTY|${condition.conditionOperator}|${sortedIds}`;
    }

    return '';
  }, []);

  const validateForm = useCallback(
    (values: FormValues): FormErrors => {
      const errors: FormErrors = {};

      if (!values.name?.trim()) {
        errors.name = translate('errors.ruleNameRequired');
      } else {
        const ruleName = values.name.trim();
        const duplicateRule = existingRules.find(
          (rule) =>
            rule.name === ruleName &&
            (!initialValues || rule.id !== initialValues.id)
        );

        if (duplicateRule) {
          errors.name = translate('errors.ruleNameExists');
        }
      }

      const approversValidation = getApproversError(
        selectedMode,
        selectedApprovers.length
      );
      setApproversError(approversValidation);
      if (approversValidation) {
        errors.approvers = approversValidation;
      }

      // Validate conditions
      if (
        !isFallbackRule &&
        values.conditions &&
        values.conditions.length > 0
      ) {
        values.conditions.forEach((condition: Condition, index: number) => {
          if (condition.conditionType === ConditionType.EXPENSE) {
            if (!condition.values || condition.values.length === 0) {
              errors.conditions ??= [];
              errors.conditions[index] ??= {};
              errors.conditions[index].values = 'Required';
            }
          } else if (condition.conditionType === ConditionType.COUNTERPARTY) {
            if (
              condition.conditionOperator === CounterpartyOperator.IS ||
              condition.conditionOperator === CounterpartyOperator.IS_ANY_OF
            ) {
              if (!condition.values || condition.values.length === 0) {
                errors.conditions ??= [];
                errors.conditions[index] ??= {};
                errors.conditions[index].values = 'Required';
              }
            }
          } else if (condition.conditionType === ConditionType.AMOUNT) {
            if (!condition.values[0] || condition.values[0].trim() === '') {
              errors.conditions ??= [];
              errors.conditions[index] ??= {};
              errors.conditions[index].amountFrom = 'Required';
            }

            const isBetween =
              condition.conditionOperator === AmountOperator.BETWEEN;
            if (
              isBetween &&
              (!condition.values[1] || condition.values[1].trim() === '')
            ) {
              errors.conditions ??= [];
              errors.conditions[index] ??= {};
              errors.conditions[index].amountTo = 'Required';
            }
          }
        });

        // Duplicate condition validation
        const seenKeys = new Set<string>();
        let hasDuplicate = false;

        for (const condition of values.conditions) {
          const key = normalizeConditionKey(condition);
          if (key && !seenKeys.add(key)) {
            hasDuplicate = true;
            break;
          }
        }

        if (hasDuplicate) {
          errors.conditionsDuplicate = translate('errors.duplicateCondition');
        }
      }

      return errors;
    },
    [
      selectedMode,
      selectedApprovers.length,
      getApproversError,
      translate,
      existingRules,
      initialValues,
      isFallbackRule,
      normalizeConditionKey
    ]
  );

  type FormValues = {
    name: string;
    description: string;
    conditions: Condition[];
    exclusive: boolean;
    active: boolean;
  };

  const transformConditionsToApi = useCallback(
    (conditions: Condition[]): ApprovalConditionCreatePatch[] => {
      return conditions.map((condition) => {
        if (condition.conditionType === ConditionType.AMOUNT) {
          const amountFrom = Number.parseFloat(condition.values[0] || '0');
          const isBetweenOperator =
            condition.conditionOperator === AmountOperator.BETWEEN;
          const amountTo =
            isBetweenOperator && condition.values[1]
              ? Number.parseFloat(condition.values[1])
              : undefined;
          const currencyIsoCode = isBetweenOperator
            ? condition.values[2] || 'EUR'
            : condition.values[1] || 'EUR';
          const currency = findCurrencyByIsoCode(currencyIsoCode);

          const amountCondition: AmountConditionCreatePatch = {
            conditionType: 'AMOUNT',
            operator: condition.conditionOperator,
            amountFrom,
            currencyId: currency?.id || 1
          };

          if (
            amountTo !== undefined &&
            amountCondition.operator === AmountOperator.BETWEEN
          ) {
            amountCondition.amountTo = amountTo;
          }

          return amountCondition;
        }

        if (condition.conditionType === ConditionType.EXPENSE) {
          const expenseTypeIds = condition.values.map(
            (exp: InvoiceExpenseType) => exp.id
          );
          return {
            conditionType: 'EXPENSE',
            operator: condition.conditionOperator,
            expenseTypeIds
          };
        }

        if (condition.conditionType === ConditionType.COUNTERPARTY) {
          const operator = condition.conditionOperator;
          const counterpartyIds =
            operator === CounterpartyOperator.IS ||
            operator === CounterpartyOperator.IS_ANY_OF
              ? condition.values.map((c: any) => c.id)
              : undefined;

          const counterpartyCondition: CounterpartyConditionCreatePatch = {
            conditionType: 'COUNTERPARTY',
            operator
          };

          if (counterpartyIds !== undefined) {
            counterpartyCondition.counterpartyIds = counterpartyIds;
          }

          return counterpartyCondition;
        }

        return {
          conditionType: ConditionType.EXPENSE,
          operator: ExpenseOperator.IS,
          expenseTypeIds: []
        };
      });
    },
    [findCurrencyByIsoCode]
  );

  const createApprovalFlow = useCallback((): ApprovalFlowCreatePatch => {
    if (selectedMode === ApprovalMode.AUTO_APPROVE) {
      return {
        approvalMode: ApprovalMode.AUTO_APPROVE
      };
    }

    const steps = selectedApprovers.map((appr, index) => ({
      stepOrder: index + 1,
      approverId: appr.id
    }));

    if (selectedMode === ApprovalMode.SEQUENTIAL) {
      return {
        approvalMode: ApprovalMode.SEQUENTIAL,
        steps
      };
    }

    return {
      approvalMode: ApprovalMode.PARALLEL,
      minimumApprovalsRequired: requiredApprovals,
      steps
    };
  }, [selectedMode, selectedApprovers, requiredApprovals]);

  const clearApproversError = useCallback(() => {
    setApproversError('');
  }, []);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (getApproversError(selectedMode, selectedApprovers.length)) {
        return;
      }

      // Block submission if duplicate conditions exist
      const seenKeys = new Set<string>();
      let hasDuplicate = false;
      for (const condition of values.conditions) {
        const key = normalizeConditionKey(condition);
        if (key && !seenKeys.add(key)) {
          hasDuplicate = true;
          break;
        }
      }
      if (hasDuplicate) {
        return;
      }

      if (onSave) {
        const body: ApprovalRuleCreate = {
          name: values.name,
          description: values.description || undefined,
          conditions: transformConditionsToApi(values.conditions),
          flow: createApprovalFlow(),
          exclusive: values.exclusive,
          active: values.active
        };

        onSave(body);
      }
    },
    [
      onSave,
      transformConditionsToApi,
      createApprovalFlow,
      selectedMode,
      selectedApprovers,
      getApproversError,
      normalizeConditionKey
    ]
  );

  const formInitialValues = useMemo(
    () => ({
      name: initialValues?.name || '',
      description: initialValues?.description || '',
      conditions: initialValues?.conditions
        ? transformConditionsToLocal(initialValues.conditions)
        : [],
      exclusive: initialExclusive ?? false,
      active: initialActive ?? true
    }),
    [initialValues, initialExclusive, initialActive, transformConditionsToLocal]
  );

  const initialApproversList = initialSelectedApprovers ?? [];
  const hasApprovalFlowChanges =
    selectedMode !== (initialSelectedMode || defaultMode) ||
    selectedApprovers.length !== initialApproversList.length ||
    selectedApprovers.some(
      (approver, index) => approver.id !== initialApproversList[index]?.id
    ) ||
    (selectedMode === ApprovalMode.PARALLEL &&
      requiredApprovals !== (initialRequiredApprovals ?? 1));

  return (
    <Box sx={formWrapperStyle}>
      <Formik<FormValues>
        initialValues={formInitialValues}
        validate={validateForm}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ submitForm, setFieldValue, errors, dirty }) => {
          submitFormRef.current = submitForm;
          formikSetFieldValueRef.current = setFieldValue;
          const hasDuplicateConditions = !!(errors as FormErrors)
            .conditionsDuplicate;
          return (
            <>
              <UnsavedChangesSync isDirty={dirty || hasApprovalFlowChanges} />
              <Box
                sx={{ ...stickyFormHeaderWrapperStyle, ...stickyWrapperStyle }}
              >
                <ApprovalRuleFormHeader
                  isEdit={isEdit}
                  ruleName={initialValues?.name}
                  isFallback={isFallback}
                  onBack={() => requestNavigation(onCancel)}
                  onSave={() => submitFormRef.current?.()}
                  onTestRules={() => {
                    openSimulator();
                  }}
                  isSaveDisabled={hasDuplicateConditions}
                />
              </Box>

              <Box sx={scrollableFormContainerStyle}>
                {isFallbackRule && (
                  <Box sx={fallbackWarningContentStyle}>
                    <Warning sx={fallbackWarningIconStyle} />
                    <Box display="flex" flexDirection="column" gap={0.75}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#663c00',
                          fontWeight: 600
                        }}
                      >
                        {translate('approvals.fallbackWarning')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#663c00'
                        }}
                      >
                        {translate('approvals.fallbackWarningHint')}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!isFallbackRule && <RuleBasicInfoSection />}

                <ConditionsSection
                  expenseTypes={expenseTypes}
                  counterparties={counterparties}
                  currencies={currencies}
                  defaultCurrencyId={company?.currencyId}
                  isFallback={isFallbackRule}
                  stepNumber={conditionsStepNumber}
                  hasDuplicateConditions={hasDuplicateConditions}
                />

                <ApprovalModeSection
                  stepNumber={approvalModeStepNumber}
                  selectedMode={selectedMode}
                  setSelectedMode={setSelectedMode}
                  selectedApprovers={selectedApprovers}
                  setSelectedApprovers={setSelectedApprovers}
                  requiredApprovals={requiredApprovals}
                  setRequiredApprovals={setRequiredApprovals}
                  approvers={approvers}
                  approversError={approversError}
                  onClearApproversError={clearApproversError}
                  isFallback={isFallbackRule}
                />

                {!isFallbackRule && (
                  <RuleOptionsSection selectedMode={selectedMode} />
                )}
              </Box>

              <Box
                sx={{
                  ...stickyLivePreviewWrapperStyle,
                  ...stickyWrapperStyle
                }}
              >
                <LivePreviewSection
                  selectedMode={selectedMode}
                  selectedApprovers={selectedApprovers}
                  requiredApprovals={requiredApprovals}
                />
              </Box>
            </>
          );
        }}
      </Formik>
      <ApprovalRuleSimulatorModal
        isOpen={isSimulatorOpen}
        onClose={closeSimulator}
        companyId={companyId}
        expenseTypes={expenseTypes}
        counterparties={counterparties}
        currencies={currencies}
        defaultCurrencyId={company?.currencyId}
        activeRulesCount={existingRules.filter((r) => r.active).length}
      />
    </Box>
  );
};

function transformConditionsToLocal(
  conditions: ApprovalCondition[]
): Condition[] {
  return conditions.map((condition) => {
    if (condition.conditionType === ConditionType.AMOUNT) {
      const amountCondition = condition;
      if (condition.operator === AmountOperator.BETWEEN) {
        return {
          id: `condition_${Date.now()}_${Math.random()}`,
          conditionType: ConditionType.AMOUNT,
          conditionOperator: amountCondition.operator,
          values: [
            amountCondition.amountFrom.toString(),
            amountCondition.amountTo?.toString() || '',
            amountCondition.currency.isoCode || 'EUR'
          ]
        };
      }
      return {
        id: `condition_${Date.now()}_${Math.random()}`,
        conditionType: ConditionType.AMOUNT,
        conditionOperator: amountCondition.operator,
        values: [
          amountCondition.amountFrom.toString(),
          amountCondition.currency.isoCode || 'EUR',
          ''
        ]
      };
    }

    if (condition.conditionType === ConditionType.EXPENSE) {
      const expenseCondition = condition;
      return {
        id: `condition_${Date.now()}_${Math.random()}`,
        conditionType: ConditionType.EXPENSE,
        conditionOperator: expenseCondition.operator,
        values: [...expenseCondition.expenseTypes]
      };
    }

    if (condition.conditionType === ConditionType.COUNTERPARTY) {
      const counterpartyCondition = condition;
      return {
        id: `condition_${Date.now()}_${Math.random()}`,
        conditionType: ConditionType.COUNTERPARTY,
        conditionOperator: counterpartyCondition.operator,
        values:
          counterpartyCondition.counterparties
            ?.filter((c: any) => c.id !== undefined)
            .map((c: any) => ({
              id: c.id!,
              name: c.name || ''
            })) || []
      };
    }

    return {
      id: `condition_${Date.now()}_${Math.random()}`,
      conditionType: ConditionType.EXPENSE,
      conditionOperator: ExpenseOperator.IS,
      values: []
    };
  });
}
